import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useFetchData, usePostData } from '@/api/apiHooks';
import {
  clearConnectRedirectLocation,
  clearReconnectConnectionId,
  saveConnectRedirectLocation,
  saveReconnectConnectionId,
} from '@/utils/marketingConnectRedirect';
import {
  RecruitmentMarketingAccountsResponse,
  RecruitmentMarketingAuthUrlRequest,
  RecruitmentMarketingAuthUrlResponse,
  RecruitmentMarketingConnectionRequest,
  RecruitmentMarketingConnectionResponse,
  RecruitmentMarketingExternalAccountRequest,
  RecruitmentMarketingProvider,
} from '@/utils/types';
import { URLS } from '@/utils/urls';

import { ConnectionStatus } from './ConnectionStatusCard';
import { RECRUITMENT_MARKETING_PROVIDER_DETAILS } from './recruitmentMarketingProviders';

const CONNECTION_STATUS_MAP: Record<string, ConnectionStatus> = {
  connected: 'connected',
  expiring_soon: 'expiringSoon',
  expired: 'expired',
  error: 'error',
};

const getErrorMessage = (provider: RecruitmentMarketingProvider) =>
  `Unable to verify connection with ${RECRUITMENT_MARKETING_PROVIDER_DETAILS[provider].shortName}. Try reconnecting your account.`;

export const mapConnectionStatus = (status?: string): ConnectionStatus => {
  if (!status) return 'notConnected';
  return CONNECTION_STATUS_MAP[status.toLowerCase()] ?? 'notConnected';
};

export const useGenerateAuthUrl = (provider: RecruitmentMarketingProvider) =>
  usePostData<
    RecruitmentMarketingAuthUrlResponse,
    RecruitmentMarketingAuthUrlRequest
  >({
    url: URLS.generateRecruitmentMarketingAuthUrl(provider),
    errorMessage: getErrorMessage(provider),
  });

export const useCreateRecruitmentMarketingConnection = (
  provider: RecruitmentMarketingProvider
) =>
  usePostData<
    RecruitmentMarketingConnectionResponse,
    RecruitmentMarketingConnectionRequest
  >({
    url: URLS.createRecruitmentMarketingConnection(provider),
    errorMessage: getErrorMessage(provider),
  });

export const useReconnectRecruitmentMarketingConnection = (
  provider: RecruitmentMarketingProvider,
  connectionId: string
) =>
  usePostData<
    RecruitmentMarketingConnectionResponse,
    RecruitmentMarketingConnectionRequest
  >({
    url: URLS.reconnectRecruitmentMarketingConnection(connectionId),
    method: 'PUT',
    errorMessage: getErrorMessage(provider),
  });

export const useRecruitmentMarketingAccounts = (
  connectionId: string,
  enabled: boolean
) =>
  useFetchData<RecruitmentMarketingAccountsResponse>({
    url: URLS.getRecruitmentMarketingAccounts(connectionId),
    enabled: enabled && !!connectionId,
  });

export const useSaveAccountSettings = (
  provider: RecruitmentMarketingProvider,
  connectionId: string
) =>
  usePostData<void, RecruitmentMarketingExternalAccountRequest>({
    url: URLS.setRecruitmentMarketingPixel(connectionId),
    method: 'PUT',
    errorMessage: getErrorMessage(provider),
  });

export const useDeleteProviderAccount = (
  provider: RecruitmentMarketingProvider,
  connectionId: string
) =>
  usePostData<void>({
    url: URLS.deleteRecruitmentMarketingAccount(connectionId),
    method: 'DELETE',
    errorMessage: `Failed to delete ${RECRUITMENT_MARKETING_PROVIDER_DETAILS[provider].shortName} account. Please try again.`,
  });

/**
 * The one reconnect flow, shared by the card's "Reconnect" action and the
 * modal's "Change account". Keeping the existing connection id is what makes
 * the callback issue PUT /reconnect instead of opening a second connection.
 */
export const useProviderReconnect = (
  provider: RecruitmentMarketingProvider,
  companyId: string,
  panelId: string
) => {
  const location = useLocation();
  const { mutate: generateAuthUrl, isPending: isReconnecting } =
    useGenerateAuthUrl(provider);

  const reconnect = useCallback(
    (connectionId: string, options?: { onError?: () => void }) => {
      if (!connectionId) return;
      saveConnectRedirectLocation(`${location.pathname}${location.search}`);
      saveReconnectConnectionId(connectionId);

      generateAuthUrl(
        { company_id: companyId, panel_id: panelId },
        {
          onSuccess: (response) => {
            window.location.href = response.data.auth_url;
          },
          onError: () => {
            // We never leave the page, so don't leave a pending reconnect behind.
            clearConnectRedirectLocation();
            clearReconnectConnectionId();
            options?.onError?.();
          },
        }
      );
    },
    [companyId, panelId, generateAuthUrl, location.pathname, location.search]
  );

  return { reconnect, isReconnecting };
};
